import { useEffect } from 'react';
import { useMap } from 'react-map-gl/maplibre';
import { Badge, IconButton, Typography } from '@mui/material';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';

// Capas de FIRs/UIRs (líneas + etiquetas) definidas en public/map-styles/kairos/*.json
const FIR_LAYERS = ['fir_boundaries', 'fir_labels'];
// Nombres de países del estilo base: ocultos mientras se ven los FIRs, visibles al desactivarlos
const COUNTRY_LABELS = ['place_country_2', 'place_country_1'];
// Solo los nombres quedan por encima de las capas meteorológicas, en este orden
const TOP_LAYERS = ['fir_labels', ...COUNTRY_LABELS];

// Las capas meteorológicas (heatmaps/polygons) se añaden en runtime encima del estilo base:
// fir_fill y fir_boundaries se quedan en su sitio del estilo, por debajo de ellas.
// Los nombres sí se vuelven a subir arriba del todo en cada cambio de estilo para que no los tapen.
const raiseLabels = ({ target: map }) => {
  if (!map.getLayer(TOP_LAYERS[0])) return;
  if (map.getLayersOrder().slice(-TOP_LAYERS.length).join() !== TOP_LAYERS.join()) {
    TOP_LAYERS.forEach((id) => map.moveLayer(id));
  }
};

const setVisibility = (map, ids, on) =>
  ids.forEach((id) => map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'));

// Tamaño aproximado del FIR a partir de su bbox (grados² corregidos por latitud)
const bboxArea = ({ bbox: [w, s, e, n] }) => (e - w) * (n - s) * Math.cos(((s + n) / 2) * (Math.PI / 180));

// "rank" 1 = FIR más grande: el filter de fir_labels en el estilo decide cuántos se ven por zoom.
// Los pares FIR/UIR del mismo nombre (MADRID FIR / MADRID UIR) comparten centro y se pisarían:
// de cada par se queda solo el FIR.
const rankFirs = (firs) => {
  const byName = new Map();
  for (const fir of firs) {
    const key = fir.name.replace(/ (FIR|UIR)$/, '');
    if (!byName.has(key) || byName.get(key).visibility === 'upper') byName.set(key, fir);
  }
  return [...byName.values()]
    .sort((a, b) => bboxArea(b) - bboxArea(a))
    .map((fir, i) => ({ ...fir, rank: i + 1 }));
};

// firs_meta.json no es GeoJSON: su "center" [lon, lat] se usa como punto de la etiqueta
const loadFirLabels = (map) =>
  fetch('/static/data/firs_meta.json')
    .then((res) => res.json())
    .then((firs) => {
      const data = {
        type: 'FeatureCollection',
        features: rankFirs(firs).map(({ center, ...properties }) => ({
          type: 'Feature',
          properties,
          geometry: { type: 'Point', coordinates: center },
        })),
      };
      const apply = () => map.getSource('fir_labels')?.setData(data);
      if (map.getSource('fir_labels')) apply();
      else map.once('style.load', apply);
    })
    .catch((err) => console.warn('[FIRs] No se pudo cargar firs_meta.json:', err));

/**
 * Se pone como hijo de <Map>. Arranca en cuanto existe el mapa, sin esperar a onLoad:
 * 'load' no llega hasta que han cargado todas las teselas (WMS/heatmaps incluidos).
 * visible=false oculta los FIRs (relleno, líneas y nombres) y devuelve los nombres de países.
 */
export default function FirLayers({ visible = true }) {
  const { current } = useMap();

  useEffect(() => {
    if (!current) return undefined;
    const map = current.getMap();
    map.on('styledata', raiseLabels);
    raiseLabels({ target: map });
    loadFirLabels(map);
    return () => map.off('styledata', raiseLabels);
  }, [current]);

  useEffect(() => {
    if (!current) return undefined;
    const map = current.getMap();
    const apply = () => {
      setVisibility(map, ['fir_fill', ...FIR_LAYERS], visible);
      setVisibility(map, COUNTRY_LABELS, !visible);
    };
    if (map.getLayer('fir_fill')) apply();
    else map.once('style.load', apply);
    return () => map.off('style.load', apply);
  }, [current, visible]);

  return null;
}

// Botón SECTORS de map_left / map_right, con el mismo estilo que los toggles HEATMAPS / POLYGONS
export function FirToggle({ visible, onToggle }) {
  return (
    <Badge
      badgeContent={
        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
          SECTORS
        </Typography>
      }
      color="error"
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      sx={{
        '& .MuiBadge-badge': {
          padding: '0 8px',
          height: '18px',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          top: '0px',
          left: '-2px',
          transform: 'translateX(-100%)',
          borderRadius: '10px',
        },
      }}
    >
      <IconButton
        onClick={onToggle}
        aria-pressed={visible}
        title="Toggle FIR/UIR sectors"
        sx={{
          bgcolor: visible ? '#38a477' : '#ffffff',
          color: visible ? '#ffffff' : '#38a477',
          '&:hover': { bgcolor: visible ? '#38a477' : '#f5f5f5' },
          width: { xs: 28, md: 36 },
          height: { xs: 28, md: 36 },
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          borderRadius: 10,
          opacity: 0.9
        }}
      >
        <Grid4x4Icon sx={{ fontSize: { xs: 18, md: 22 } }} />
      </IconButton>
    </Badge>
  );
}
